import { UpdateUserInfoParams } from "@/types/user";
import http from "../services/http";
import loginInfoStore from "../store/login.store";
import userInfoStore from "../store/user.store";
import { ENCRYPT_APIS } from "./encypet_apis";

// 1 注册 2 修改手机号 3 重置密码 4 微信绑定 5 验证码登录 6 绑定手机号 7 变更企业超管 8 苹果绑定 9 注销
export enum ESendCaptchasType {
  Register = 1,
  ModifyMobile = 2,
  ResetPassword = 3,
  WeixinBind = 4,
  Login = 5,
  BindMobile = 6,
  ChangeCompanyAdmin = 7,
  AppleBind = 8,
  Destroy = 9,
}

interface ISendCaptchasError {
  errCode: number;
  errMsg: string;
}

async function sendCaptchas(mobile: string, type: ESendCaptchasType) {
  try {
    const url = ENCRYPT_APIS.sendCaptchas;
    const res = await http.post<boolean>(url, {
      mobile,
      type,
    });
    return {
      success: true,
      data: res.data,
    };
  } catch (e: unknown) {
    const error = e as ISendCaptchasError;
    console.log("sendCaptchas error >>>> ", error);
    return {
      success: false,
      data: error.errMsg,
    };
  }
}

interface IAuth {
  accessToken: string;
  refreshToken: string;
  expiresIn: 0;
  userId: string;
}

async function loginByMobile(mobile: string, mobileCaptcha: string) {
  const url = ENCRYPT_APIS.login;
  const res = await http.post<IAuth>(url, {
    mobile,
    mobileCaptcha,
  });
  http.setToken(res.data.accessToken, res.data.refreshToken, res.data.expiresIn);
  return res.data;
}

interface IUserInfo {
  userId: string;
  displayName: string;
  mobile: string;
  avatar: string;
  company: {
    identityId: string;
    identityType: 0;
    avatar: string;
    realname: string;
    companyName: string;
    shortName: string;
    companyId: string;
    companyCode: string;
  };
  switchOrg: boolean;
}

async function getUserInfo() {
  const url = ENCRYPT_APIS.getUserInfo;
  const res = await http.get<IUserInfo>(url);
  return res.data;
}

function createWxClientAuthUrl(uri: string, extraParams?: { appid?: string, scope?: string }) {
  const { appid, scope } = extraParams || {};
  const params = {
    appid: appid || import.meta.env.VITE_WX_OFFCIAL_APP_ID,
    response_type: "code",
    scope: scope || "snsapi_userinfo",
    redirect_uri: uri,
  };
  const searchParams = new URLSearchParams(params);
  return import.meta.env.VITE_PIVOT_FRONTEND_HOST + `/static/html/get-weixin-code.html?${searchParams.toString()}`;
}

async function getQrcode() {
  const res = await http.get<string>("/api/auth/weixin/qrcode", {
    params: {
      appId: import.meta.env.VITE_WX_APPID,
      uri: location.origin + "/redirect?self_redirect=true",
      href: "data:text/css;base64,Cmh0bWwsCmJvZHkgewogIGJhY2tncm91bmQtY29sb3I6ICNmZmZmZmYgIWltcG9ydGFudDsKICBtYXJnaW46IDAgIWltcG9ydGFudDsKICBvdmVyZmxvdzogaGlkZGVuOwogIHBhZGRpbmc6IDAgIWltcG9ydGFudDsKfQoudGl0bGUsCi5pbmZvIHsKICBkaXNwbGF5OiBub25lICFpbXBvcnRhbnQ7Cn0KLmltcG93ZXJCb3ggLnRpdGxlIHsKICBmb250LXNpemU6IDE0cHg7CiAgY29sb3I6ICMwMDAwMDA7Cn0KLmltcG93ZXJCb3ggLnFyY29kZSB7CiAgd2lkdGg6IDEwMCU7CiAgYm9yZGVyOiAwICFpbXBvcnRhbnQ7CiAgbWFyZ2luOiAwICFpbXBvcnRhbnQ7CiAgcGFkZGluZzogMCAhaW1wb3J0YW50OwogIHZlcnRpY2FsLWFsaWduOiBib3R0b207Cn0K",
    },
  });
  return res.data;
}

async function getSessionId() {
  try {
    const res = await http.get<string | null>("/api/auth/login/session");
    return res.data;
  } catch (error) {
    return null;
  }
}

async function loginByWxcode(params: { code: string; sessionId: string; isMobile?: boolean }): Promise<IAuth> {
  const { isMobile } = params;
  const appId = isMobile ? import.meta.env.VITE_WX_OFFCIAL_APP_ID : import.meta.env.VITE_WX_APPID;
  const res = await http.get<IAuth>(`/api/auth/weixin/token/code/${appId}?code=${params.code}`, { headers: { "x-session-id": params.sessionId } });
  http.setToken(res.data.accessToken, res.data.refreshToken, res.data.expiresIn);
  return res.data;
}

async function wxBindMobile(params: { mobile: string; captcha: string; sessionId: string }): Promise<IAuth> {
  const url = ENCRYPT_APIS.bindWeixin;
  const res = await http.post<IAuth>(url, { mobile: params.mobile, mobileCaptcha: params.captcha }, { headers: { "x-session-id": params.sessionId } });
  http.setToken(res.data.accessToken, res.data.refreshToken, res.data.expiresIn);
  return res.data;
}

async function userLogout(): Promise<boolean> {
  try {
    await http.post("/api/auth/logout", {});
    http.clearToken();
    loginInfoStore.getState().setHasLogin(false);
    userInfoStore.getState().resetUser();
    return true;
  } catch (e) {
    return false;
  }
}

async function refreshUserToken(refreshToken: string) {
  const url = ENCRYPT_APIS.refreshToken;
  const res = await http.post<IAuth>(url, {
    refreshToken,
  });
  http.setToken(res.data.accessToken, res.data.refreshToken, res.data.expiresIn);
  return res.data;
}

const updateUserInfo = (params: UpdateUserInfoParams) => {
  const url = ENCRYPT_APIS.getUserInfo;
  return http.put(url, params);
};

async function updateUserAvatar(newAvatar: string) {
  return http.post<IAuth>("/api/auth/user/replace-avatar", {
    url: newAvatar,
  });
}

async function userDestroy(captcha: string): Promise<boolean> {
  try {
    const url = ENCRYPT_APIS.cancelBusiness;
    await http.post(url, { mobileCaptcha: captcha });
    http.clearToken();
    loginInfoStore.getState().setHasLogin(false);
    userInfoStore.getState().resetUser();
    return true;
  } catch (e) {
    return false;
  }
}

async function getVisitorToken() {
  const res = await http.post("/api/open/visitor/token/general", {});
  return res.data;
}

// 修改密码
async function updatePassword(params: {
  oldPassword: string;
  password: string;
  companyId: string;
  userId: string;
}) {
  const res = await http.patch(`/api/auth/password/companies/${params.companyId}/users/${params.userId}/password`, params);
  return res.data;
}

//#region 私有化登录相关接口

async function loginByPassword(params: { username: string; password: string; graphicCaptcha?: string }) {
  const res = await http.post<IAuth>("/api/auth/password", params);
  http.setToken(res.data.accessToken, res.data.refreshToken, res.data.expiresIn);
  return res.data;
}

async function getGraphicCaptcha() {
  const sessionId = http.getHttpHeader("x-session-id") as string;
  const response = await fetch(
    `${import.meta.env.VITE_BACKEND_URL}/api/auth/password/graphicCaptchas/code.jpg`,
    {
      method: "GET",
      headers: {
        "x-session-id": sessionId,
        "Content-Type": "application/json",
      },
    }
  );
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  const blob = await response.blob();
  const imageUrl = URL.createObjectURL(blob);
  return imageUrl;
}

async function registerByPassword(params: { mobile: string; password: string; confirmPassword: string }) {
  const res = await http.post<IAuth>("/api/auth/password/accounts/register-by-password", params);
  return res.data;
}

async function getXySessionId() {
  const res = await http.get<string>("/api/auth/login/session");
  http.setHttpHeader("x-session-id", res.data);
  return res.data;
}

async function getUserInfoByMobile(mobile: string) {
  const res = await http.get<IUserInfo>(`/api/auth/password/accounts/userinfo?mobile=${mobile}`);
  return res.data;
}

//#endregion

export {
  sendCaptchas,
  loginByMobile,
  getUserInfo,
  getQrcode,
  getSessionId,
  loginByWxcode,
  wxBindMobile,
  userLogout,
  createWxClientAuthUrl,
  refreshUserToken,
  updateUserInfo,
  updateUserAvatar,
  userDestroy,
  getVisitorToken,
  updatePassword,
  loginByPassword,
  getGraphicCaptcha,
  registerByPassword,
  getUserInfoByMobile,
  getXySessionId,
};
